import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Loader2 } from 'lucide-react';
import { apiService } from '@/services/api';

interface UsuarioRow {
  id: string;
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  empresa?: { id: string; nome: string } | null;
  squad?: { id: string; nome: string } | null;
}

export function AdminUsuariosList() {
  const [usuarios, setUsuarios] = useState<UsuarioRow[]>([]);
  const [squads, setSquads] = useState<{ id: string; nome: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [busca, setBusca] = useState('');
  const [ativoFiltro, setAtivoFiltro] = useState<string>('');
  const [roleFiltro, setRoleFiltro] = useState<string>('');
  const [squadFiltro, setSquadFiltro] = useState<string>('');

  useEffect(() => {
    const loadSquads = async () => {
      const res = await apiService.listarSquads({});
      if (res.success && res.data) {
        setSquads(Array.isArray(res.data) ? res.data : []);
      }
    };
    loadSquads();
  }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const res = await apiService.listarUsuarios({
        page,
        limit: 20,
        busca: busca.trim() || undefined,
        ativo: ativoFiltro === '' ? undefined : ativoFiltro === 'true',
        role: roleFiltro || undefined,
        squadId: squadFiltro || undefined,
      });
      if (res.success && res.data) {
        setUsuarios(Array.isArray(res.data) ? res.data : []);
        const pag = (res as any).pagination;
        if (pag) setTotalPages(pag.totalPages ?? 1);
      } else {
        setUsuarios([]);
      }
    } catch {
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, [page, busca, ativoFiltro, roleFiltro, squadFiltro]);

  const handleBuscaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-schmersal-gray-dark dark:text-white">
          Usuários
        </h1>
        <Link
          to="/admin/usuarios/novo"
          className="inline-flex items-center px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Novo usuário
        </Link>
      </div>

      <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-4">
        <form onSubmit={handleBuscaSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou email"
            className="px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
          />
          <select
            value={ativoFiltro}
            onChange={(e) => setAtivoFiltro(e.target.value)}
            className="px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
          >
            <option value="">Todos (ativo)</option>
            <option value="true">Ativo</option>
            <option value="false">Inativo</option>
          </select>
          <select
            value={roleFiltro}
            onChange={(e) => setRoleFiltro(e.target.value)}
            className="px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
          >
            <option value="">Todos (perfil)</option>
            <option value="admin">Admin</option>
            <option value="user">Usuário</option>
          </select>
          <select
            value={squadFiltro}
            onChange={(e) => { setSquadFiltro(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
          >
            <option value="">Todos (squad)</option>
            {squads.map((sq) => (
              <option key={sq.id} value={sq.id}>{sq.nome}</option>
            ))}
            <option value="__none__">Sem squad</option>
          </select>
          <button
            type="submit"
            className="px-4 py-2 bg-schmersal-gray-light dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white rounded-lg hover:opacity-90"
          >
            Filtrar
          </button>
        </form>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-schmersal-blue" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white">Nome</th>
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white">Email</th>
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white">Perfil</th>
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white">Ativo</th>
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white">Squad</th>
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white">Empresa</th>
                    <th className="py-3 px-2 font-semibold text-schmersal-gray-dark dark:text-white w-20">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((u) => (
                    <tr key={u.id} className="border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
                      <td className="py-3 px-2 text-schmersal-gray-dark dark:text-white">{u.nome}</td>
                      <td className="py-3 px-2 text-schmersal-gray-dark dark:text-white">{u.email}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${u.role === 'admin' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200' : 'bg-schmersal-gray-light dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white'}`}>
                          {u.role === 'admin' ? 'Admin' : 'Usuário'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-schmersal-gray-dark dark:text-white">{u.ativo ? 'Sim' : 'Não'}</td>
                      <td className="py-3 px-2">
                        {u.squad ? (
                          <span className="px-2 py-0.5 rounded text-xs bg-schmersal-blue/10 text-schmersal-blue dark:bg-schmersal-yellow/10 dark:text-schmersal-yellow">
                            {u.squad.nome}
                          </span>
                        ) : (
                          <span className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">Global</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-schmersal-gray-dark dark:text-white">{u.empresa?.nome ?? '—'}</td>
                      <td className="py-3 px-2">
                        <Link
                          to={`/admin/usuarios/${u.id}/editar`}
                          className="inline-flex p-2 text-schmersal-blue dark:text-schmersal-yellow hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark rounded"
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {usuarios.length === 0 && (
              <p className="py-8 text-center text-schmersal-gray-medium dark:text-schmersal-gray-light">
                Nenhum usuário encontrado.
              </p>
            )}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 rounded border border-schmersal-gray-light dark:border-schmersal-gray-dark disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="px-3 py-1 text-schmersal-gray-dark dark:text-white">
                  Página {page} de {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 rounded border border-schmersal-gray-light dark:border-schmersal-gray-dark disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
